import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { SubscriptionBasedComponent } from '../../../../subscription-helpers/subscription-based.component';
import ProjectMapHelper, {
  SubProjectGroup,
  MARKER_COLORS,
} from '../../../../helpers/project-map-helper';
import { RequestService } from 'src/app/services/request.service';
import { ProductTypeCriteriumService } from 'src/app/services/product-type-criterium-service';
import { merge } from 'rxjs';
import { map, startWith, switchMap } from 'rxjs/operators';
import { SubProject } from 'src/app/model/project/sub-project';
import { EmployeeChoicesService } from 'src/app/services/employee-choices.service';
import Employee from 'src/app/model/product/employee';
import GroupSelectedEvent from './helper-interfaces/group-selected-event';
import LastSelectedPlanValues from './helper-interfaces/last-selected-plan-values';
import { NgMapsInfoWindowComponent } from '@ng-maps/core/lib/directives/info-window';
import { NgMapsViewComponent } from '@ng-maps/core';
import { GoogleComponent } from '@ng-maps/google';

@Component({
  selector: 'admin-planning-map',
  templateUrl: './admin-planning-map.component.html',
  styleUrls: ['./admin-planning-map.component.scss'],
})
export class AdminPlanningMapComponent
  extends SubscriptionBasedComponent
  implements OnInit, OnDestroy, OnChanges, AfterViewInit
{
  @Input()
  hidePlannedProjects: boolean;

  currentIW: NgMapsInfoWindowComponent;
  previousIW: NgMapsInfoWindowComponent;

  MARKER_COLORS = MARKER_COLORS;
  INPRESS_COORDINATES = ProjectMapHelper.INPRESS_COORDINATES;
  HIDE_POI_STYLE = ProjectMapHelper.HIDE_POI_STYLE;

  @ViewChild(GoogleComponent, { static: true })
  agmMap: NgMapsViewComponent<any>;
  map: google.maps.Map;

  init: boolean = false;
  reloadData = new EventEmitter<any>();
  groupSelected = new EventEmitter<GroupSelectedEvent>();

  subProjects: SubProject[] = [];
  subProjectGroups: SubProjectGroup[] = [];

  placerOptions: Employee[] = [];

  showSubmitPlanContent: boolean = true;

  protected lastSelectedPlanValues: LastSelectedPlanValues;

  constructor(
    protected requestService: RequestService,
    protected productTypeCriteriumService: ProductTypeCriteriumService,
    protected employeeChoicesService: EmployeeChoicesService
  ) {
    super();
  }

  ngOnInit(): void {
    this.employeeChoicesService.updatePlacerChoices();
    this.subscribeGetProjects();
    this.subscribeGetPlacers();
    this.subscribeProductTypeCriteriumChange();
  }

  ngAfterViewInit(): void {
    this.addSubscription(this.agmMap.mapReady, (map: google.maps.Map) => {
      this.map = map;
      this.centerMapOnBelgium(this.map);
      this.addSubscription(this.reloadData, () =>
        this.centerMapOnBelgium(this.map)
      );
      this.mapReady();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.hidePlannedProjects) {
      this.closeInfoWindow();
    }
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this.reloadData.complete();
    this.groupSelected.complete();
  }

  mapClick(): void {
    if (this.previousIW) {
      this.previousIW.close();
    }
  }

  protected mapReady() {
    // default no op
  }

  markerClick(infoWindow: NgMapsInfoWindowComponent, group): void {
    this.groupSelected.emit({
      group: group,
      lastSelectedPlanValues: this.lastSelectedPlanValues,
    });
    if (this.previousIW) {
      this.currentIW = infoWindow;
      this.previousIW.close();
    }
    this.previousIW = infoWindow;
  }

  closeInfoWindow(): void {
    if (this.previousIW != null) {
      this.previousIW.close();
      this.previousIW = null;
    }
    if (this.currentIW != null) {
      this.currentIW.close();
      this.currentIW = null;
    }
  }

  updatePlanValues(lastSelectedPlanValues: LastSelectedPlanValues) {
    this.lastSelectedPlanValues = lastSelectedPlanValues;
  }

  private subscribeGetProjects(): void {
    this.addSubscription(
      merge(this.reloadData).pipe(
        startWith({}),
        switchMap(() => {
          return this.requestService.get(
            '/subprojects/planningMap',
            this.productTypeCriteriumService.generateTypeCriteriumParam()
          );
        }),
        map((data) => {
          return data.body;
        })
      ),
      (data: SubProject[]) => {
        this.subProjects = data;
        this.processSubProjects();
        this.setInit(true);
      }
    );
  }

  private subscribeGetPlacers(): void {
    this.addSubscription(
      this.employeeChoicesService.getPlacerChoicesSubscribable(),
      (placers: Employee[]) => {
        this.placerOptions = placers;
      }
    );
  }

  private subscribeProductTypeCriteriumChange(): void {
    this.addSubscription(
      this.productTypeCriteriumService.getTypeCriteriumChangedSubscribable(),
      () => this.setInit(false)
    );
  }

  protected setInit(value: boolean): void {
    this.init = value;
    if (!value) {
      if (this.previousIW != null) {
        this.previousIW.close();
        this.previousIW = null;
      }
      this.reloadData.emit();
    }
  }

  private centerMapOnBelgium(map: google.maps.Map): void {
    map.setZoom(8);
    map.panTo(ProjectMapHelper.BELGIUM_GEOGRAPHICAL_CENTER);
  }

  protected processSubProjects(): void {
    this.subProjectGroups = ProjectMapHelper.constructSubProjectGroups(
      this.subProjects
    );
  }
}
